/**
 * S3 — pure helpers for shared delivery groups on the Scheduling Board (Final PRD §24–30, §50). No API access.
 */
export const RATIO_LABEL = { OPEN: 'Open coverage' };

export const sliceLabel = (slice) => `${slice.start_time}–${slice.end_time} ${slice.ratio === 'OPEN' ? RATIO_LABEL.OPEN : slice.ratio}`;

/** Grouped (intentional) overlap is shown as information, a real conflict as a block — never the same badge (§25). */
export const overlapIndicator = (shift) => {
  if (shift?.intentional_overlap_group_id) {
    return { kind: 'GROUPED', tone: 'info', label: 'Shared delivery', title: 'Overlapping shifts delivered together in a shared delivery group — intentional, not a conflict' };
  }
  return null;
};

const REASONS = {
  OVERLAPPING_SHIFT: 'The worker already has another shift at this time that is not part of the same shared delivery group.',
  ASSIGNMENT_BLOCKED: 'Assignment blocked by the roster conflict rule.',
  WORKER_ALREADY_COVERING: 'This worker already covers part of that window in the group.',
  COVERAGE_OUTSIDE_GROUP: 'Worker coverage must sit inside the shared window.',
  HANDOVER_OUTSIDE_COVERAGE: 'The handover time must fall inside the worker\'s coverage window.',
  MEMBERS_NOT_CONTIGUOUS: 'Member shifts must overlap or touch on the same timeline to be delivered together.',
  MEMBER_ALREADY_GROUPED: 'That shift already belongs to another shared delivery group.',
  MEMBER_ORG_MISMATCH: 'All member shifts must belong to the same organisation.',
  MEMBER_TERMINAL: 'A cancelled or completed shift cannot join a group.',
  MEMBER_NOT_PARTICIPANT_SHIFT: 'Only participant shifts can join a shared delivery group.',
  WORKER_NOT_FOUND: 'Worker not found or inactive.',
  INVALID_TIMES: 'Times must be HH:MM with the end after the start.',
};

/** Conflict / validation reason text from an API error (block codes preferred over the envelope code). */
export const groupErrorMessage = (err) => {
  const d = err?.response?.data?.detail || err?.response?.data?.error || err?.detail || {};
  const blockCodes = d?.details?.block_codes || [];
  const code = blockCodes[0] || (typeof d === 'string' ? d : d?.code);
  if (code && REASONS[code]) {
    const existing = d?.details?.blocks?.[0]?.details?.existingTime;
    return existing ? `${REASONS[code]} (${existing})` : REASONS[code];
  }
  return d?.message || code || 'Shared delivery action failed';
};

/** Coverage rows for display: live assignments sorted by start, with open gaps already materialised by the server slices. */
export const liveAssignments = (group) => (group?.worker_assignments || [])
  .filter((a) => a.status === 'ASSIGNED' || a.status === 'OPEN')
  .sort((a, b) => (a.start_abs || 0) - (b.start_abs || 0));

/** 2:1 is two assignments — count distinct ASSIGNED workers overlapping any slice with ratio W:1 where W >= 2. */
export const hasTwoToOne = (group) => (group?.ratio_slices || []).some((s) => /^[2-9]:1$/.test(s.ratio));

/** Candidate member shifts: same date, participant shift, not yet grouped, not terminal, not already a member. */
export const memberCandidates = (shifts, group) => (shifts || []).filter((s) =>
  s.participant_id && !s.shared_group_id && !(group?.member_shift_ids || []).includes(s.id)
  && !['CANCELLED', 'COMPLETED', 'NO_SHOW'].includes(String(s.status || '').toUpperCase())
  && (!group || s.date === group.window?.date || s.date === group.window?.end_date));
